import { CONTENT_TYPES, EVENT_NAME, SYNCING_STATUS } from "constants/phase";
import { STUDIO_TEMPLATE_SEGMENT_TYPE_OPTIONS } from "constants/studio";
import { APP_PAGE } from "constants/v2/appConstants";
import _ from "lodash";

import amplitude from "../externals/amplitude";
import { setDataDogUserProperties } from "../externals/datadog";
import { formatDate } from "./date";
import LocalStorage from "./LocalStorage";

export { default as fetch } from "./fetch";

export const setUserProperties = (userDetails) => {
  const email = LocalStorage.getItem("email");
  const updatedUserDetails = email ? { ...userDetails, otp_email: email } : userDetails;
  setDataDogUserProperties(updatedUserDetails);
  amplitude.updateUserData(updatedUserDetails);
};

export const addStylesheet = (url) => {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = url;
  document.head.appendChild(link);
};

export const arraysMatch = (array1, array2, key = "clipLanguage") => {
  // Helper function to check if all objects in an array either don't have the key or have the same value for the key
  const isConsistentWithValue = (array, value) => {
    return _.every(array, (item) => !_.has(item, key) || item[key] === value);
  };

  // If array2 is empty, only validate array1 for consistency
  if (_.isEmpty(array2)) {
    // Get the first available key value in array1 (if any), and check consistency
    const firstValue = array1.find((item) => _.has(item, key))?.[key];
    return isConsistentWithValue(array1, firstValue);
  }

  // If array2 has data, we only care about array2[0].clipLanguage
  const clipLanguageInArray2 = array2[0]?.[key];

  // Check if array1 is consistent with array2[0].clipLanguage
  return isConsistentWithValue(array1, clipLanguageInArray2);
};

export const capitalizeFirstLetter = (str) => str?.charAt(0)?.toUpperCase() + str?.slice(1);

export const createDynamicTemplateObject = ({ formFields, setValues, dynamicVariables, segmentType }) => {
  try {
    const segmentTypeOptions =
      segmentType && STUDIO_TEMPLATE_SEGMENT_TYPE_OPTIONS[segmentType]
        ? STUDIO_TEMPLATE_SEGMENT_TYPE_OPTIONS[segmentType]
        : [];

    const hasSegments = Array.isArray(segmentTypeOptions) && segmentTypeOptions.length > 0;

    const processFormFields = (fields, sets) => {
      // 1. Convert formFields to object if needed
      let formFieldsObject = fields;

      // 2. Merge setValues
      if (sets && typeof sets === "object") {
        Object.assign(formFieldsObject, sets);
      }

      // 3. Apply dynamic fields logic
      const dynamicFields = dynamicVariables?.dynamic_fields || [];
      if (dynamicFields.length === 0) return formFieldsObject;

      const dynamicFieldKeys = dynamicFields.map((field) => field.key);
      const filtered = Object.fromEntries(
        Object.entries(formFieldsObject).filter(([key]) => dynamicFieldKeys.includes(key)),
      );

      // 4. Process special fields
      const processed = Object.entries(filtered).map(([key, value]) => {
        // Handle player or scoring breakdown fields
        if (key === "scoring_player_name") {
          return [key, formFieldsObject["scoring_player_breakdown"]];
        }

        const playerMatch = key.match(/player_(\d+)_name/);
        if (playerMatch) {
          const breakdownKey = `player_${playerMatch[1]}_breakdown`;
          if (formFieldsObject[breakdownKey]) {
            return [key, formFieldsObject[breakdownKey]];
          }
        }

        // Handle date formatting
        if (key === "date_dd_mmm_yyyy") return [key, formatDate(value, "DD_MMM_YYYY")];
        if (key === "date_dd_mm_yyyy") return [key, formatDate(value, "DD_MM_YYYY")];

        return [key, value];
      });

      return Object.fromEntries(processed);
    };

    // 5. Handle segment-based templates or single template
    if (hasSegments) {
      return segmentTypeOptions.reduce((acc, option) => {
        acc[option.value] = processFormFields(formFields[option.value], setValues[option.value]);
        return acc;
      }, {});
    }

    return processFormFields(formFields, setValues);
  } catch (error) {
    console.error("Error in createDynamicTemplateObject:", error);
  }
};

export const transformVariantToPayload = (variant) => {
  return {
    height: Number(variant?.height) || 0,
    width: Number(variant?.width) || 0,
    scanType: variant?.scanType === "PROGRESSIVE" ? variant.scanType : variant?.interlacedType,
    fps: variant?.fpsSelect === "INITIALIZE_FROM_SOURCE" ? variant.fpsSelect : Number(variant?.fps),
    videoBitrate: Number(variant?.videoBitrate),
    ...(variant?.segmentDuration ? { segmentDuration: Number(variant?.segmentDuration) } : {}),
    bitrateType: variant?.bitrateType || null,
  };
};

export const validateTransformData = ({
  transformVideoData,
  transformVideoDataErrors,
  hlsConfiguration,
  hlsConfigurationErrors,
}) => {
  const errors = { ...transformVideoDataErrors };
  const hlsErrors = { ...hlsConfigurationErrors };

  if (transformVideoData?.outputType !== "HLS") {
    if (!transformVideoData?.height && transformVideoData?.width) {
      errors.height = "Please enter height";
    }

    if (!transformVideoData?.width && transformVideoData?.height) {
      errors.width = "Please enter width";
    }

    if (!transformVideoData?.scanType) {
      errors.scanType = "Scan type is required";
    }

    if (transformVideoData?.scanType === "INTERLACED" && !transformVideoData?.interlacedType) {
      errors.interlacedType = "Interlaced type is required";
    }

    if (
      transformVideoData?.scanType === "INTERLACED" &&
      transformVideoData?.height &&
      transformVideoData?.height % 4 !== 0
    ) {
      errors.height = "Height must be divisible by 4 for interlaced scan type";
    }

    if (!transformVideoData?.fpsSelect) {
      errors.fpsSelect = "Field is required";
    }

    if (transformVideoData?.fpsSelect === "Specified" && !transformVideoData?.fps) {
      errors.fps = "Field is required";
    }

    if (!transformVideoData?.videoBitrate) {
      errors.videoBitrate = "Bitrate is required";
    }
  }

  // Common validations for all output types
  if (!transformVideoData?.outputType) {
    errors.outputType = "Video format is required";
  }

  if (transformVideoData?.outputType === "MXF" && !transformVideoData?.videoCodec) {
    errors.videoCodec = "Video codec is required";
  }

  if (
    transformVideoData?.advanceAudio &&
    (!transformVideoData?.lkfsGate || !transformVideoData?.targetLKFS || !transformVideoData?.audioAlgorithm)
  ) {
    if (!transformVideoData?.lkfsGate) {
      errors.lkfsGate = "LKFS gate is required";
    }

    if (!transformVideoData?.targetLKFS) {
      errors.targetLKFS = "Target LKFS is required";
    }

    if (!transformVideoData?.audioAlgorithm) {
      errors.audioAlgorithm = "Audio algorithm is required";
    }
  }

  // Validate HLS configuration when outputType is HLS
  if (transformVideoData?.outputType === "HLS") {
    if (hlsConfiguration && hlsConfiguration?.length > 0) {
      hlsConfiguration.forEach((variant) => {
        const variantError = hlsErrors[variant.id] || {};

        if (!variant?.height && variant?.width) {
          variantError.height = "Please enter height";
        }

        if (!variant?.width && variant?.height) {
          variantError.width = "Please enter width";
        }

        if (!variant?.scanType) {
          variantError.scanType = "Scan type is required";
        }

        if (variant?.scanType === "INTERLACED" && !variant?.interlacedType) {
          variantError.interlacedType = "Interlaced type is required";
        }

        if (variant?.scanType === "INTERLACED" && variant?.height && variant?.height % 4 !== 0) {
          variantError.height = "Height must be divisible by 4 for interlaced scan type";
        }

        if (!variant?.fpsSelect) {
          variantError.fpsSelect = "FPS is required";
        }

        if (variant?.fpsSelect === "Specified" && !variant?.fps) {
          variantError.fps = "FPS is required";
        }

        if (!variant?.videoBitrate) {
          variantError.videoBitrate = "Bitrate is required";
        }

        if (!variant?.bitrateType) {
          variantError.bitrateType = "Bitrate type is required";
        }

        if (Object.keys(variantError).length > 0) {
          hlsErrors[variant?.id] = variantError;
        }
      });
    } else {
      errors.hlsConfiguration = "At least one variant is required";
    }
  }

  const hasTransformErrors = Object.keys(errors).length > 0;
  const hasHlsErrors = Object.values(hlsErrors).some((variantError) => Object.keys(variantError).length > 0);

  const isValid = !hasTransformErrors && !hasHlsErrors;

  return { errors, hlsErrors, isValid };
};

export const createPreviewVideoUrl = (params) => {
  try {
    const urlParams = new URLSearchParams(params);

    return `${window.location.origin}/preview-video?${urlParams.toString()}`;
  } catch (error) {
    console.error(error);
  }
};

export const getClipDetailsByAspectRatio = (clipDetails, aspectRatio, options = {}) => {
  if (!clipDetails) {
    return null;
  }

  // Extract values from clipDetails
  const clipVideoUrl = clipDetails?.videoUrl;
  const clipVideoThumbnailUrl = clipDetails?.videoThumbnailUrl || clipDetails?.thumbnail;
  const clipVideoThumbnailUrls = clipDetails?.videoThumbnailUrls;
  const editedVideos = clipDetails?.editedVideos || [];
  const clipAspectRatio = clipDetails?.aspectRatio;

  // Extract options with defaults from clipDetails
  const isDynamicClips = options.isDynamicClips ?? clipDetails?.isDynamicClips ?? false;
  const dynamicClipDocumentId = options.dynamicClipDocumentId ?? clipDetails?.dynamicClipDocumentId ?? null;
  const isStatusCheckDisabled = options.isStatusCheckDisabled ?? clipDetails?.isStatusCheckDisabled ?? false;
  const includeDynamicClips = options.includeDynamicClips ?? false;

  // Handle dynamic clips case
  if (isDynamicClips && !!dynamicClipDocumentId) {
    return editedVideos?.find(
      ({ documentId, event }) => documentId === dynamicClipDocumentId && event === EVENT_NAME.DYNAMIC,
    );
  }

  // Handle matching aspect ratio case - return basic video/thumbnail object
  if (aspectRatio === clipAspectRatio) {
    return {
      video_url: clipVideoUrl,
      thumbnailUrl: clipVideoThumbnailUrl,
      thumbnails: clipVideoThumbnailUrls || [],
    };
  }

  // Handle status check disabled case
  if (isStatusCheckDisabled) {
    return editedVideos?.find(
      ({ aspect_ratio, event }) => aspect_ratio === aspectRatio && event !== EVENT_NAME.DYNAMIC,
    );
  }

  // Default case: find edited video by aspect ratio, excluding processing status and optionally dynamic events
  return editedVideos?.find(({ aspect_ratio, status, event }) => {
    const baseCondition = aspect_ratio === aspectRatio && status !== SYNCING_STATUS.PROCESSING;

    return includeDynamicClips ? baseCondition : baseCondition && event !== EVENT_NAME.DYNAMIC;
  });
};

export const getPreviewUrls = ({
  contentIds = [],
  contentType,
  variantEntities,
  clipListEntities,
  clipEntities,
  entityId,
  aspectRatio,
  page,
}) => {
  const isHighlight = page === APP_PAGE.HIGHLIGHT_EDITOR || contentType === CONTENT_TYPES.HIGHLIGHT;

  if (contentType === CONTENT_TYPES.VARIANT) {
    return contentIds.map((contentId) => {
      const variantDetails = variantEntities?.[contentId]?.variant;
      const params = {
        variantId: variantDetails?.id,
        contentId: variantDetails?.contentId,
        contentType: variantDetails?.contentType,
        streamId: variantDetails?.content?.streamId,
        title: variantDetails?.content?.episodeTitle,
        duration: variantDetails?.content?.duration,
        aspectRatio: variantDetails?.aspectRatio,
        videoUrl: variantDetails?.url || variantDetails?.videoUrl,
        entityId,
      };
      const previewUrl = createPreviewVideoUrl(params) || variantDetails?.url || variantDetails?.videoUrl;

      return { id: contentId, previewUrl };
    });
  }

  if (contentType === CONTENT_TYPES.EXPORTED_CLIP) {
    return contentIds.map((contentId) => ({ id: contentId, previewUrl: "" }));
  }

  if (isHighlight) {
    return contentIds.map((contentId) => {
      const clipListDetails = clipListEntities?.[contentId];
      const params = {
        clipListId: clipListDetails?._id,
        aspectRatio: clipListDetails?.aspectRatio,
        previewData: JSON.stringify({
          previewUrl: clipListDetails?.previewData?.previewUrl || clipListDetails?.previewUrl,
          duration: clipListDetails?.previewData?.duration || clipListDetails?.generatedDuration,
        }),
      };
      const previewUrl =
        createPreviewVideoUrl(params) || clipListDetails?.previewUrl || clipListDetails?.previewData?.previewUrl;

      return { id: contentId, previewUrl };
    });
  }

  if (contentType === CONTENT_TYPES.CLIP) {
    return contentIds.map((contentId) => {
      const clipDetails = clipEntities?.[contentId];
      const clipDurationByAspectRatio = getClipDetailsByAspectRatio(clipDetails, aspectRatio || "16:9");
      const params = {
        clipId: clipDetails?._id,
        streamId: clipDetails?.streamId,
        title: clipDetails?.episodeTitle,
        duration: clipDurationByAspectRatio?.duration || clipDetails?.duration,
        aspectRatio: aspectRatio || clipDetails?.aspectRatio,
        videoUrl: clipDurationByAspectRatio?.video_url || clipDetails?.editedClipData?.previewUrl,
        entityId,
      };
      const previewUrl = createPreviewVideoUrl(params);

      return { id: contentId, previewUrl };
    });
  }

  return [];
};

export const addBlanksInTranscriptWords = (words) => {
  if (!Array.isArray(words) || words.length === 0) return words;

  const result = [];
  for (let i = 0; i < words.length - 1; i++) {
    result.push(words[i]);

    const timeDifference = words[i + 1].start - words[i].end;
    if (timeDifference > 1) {
      const dashCount = Math.ceil(timeDifference);
      const dashes = "─".repeat(dashCount);
      result.push({
        word: dashes,
        start: words[i].end + 0.1,
        end: words[i + 1].start - 0.1,
        confidence: 1,
        punctuated_word: dashes,
      });
    }
  }
  result.push(words[words.length - 1]);

  return result;
};

export const roundToTwoDecimals = (value) => {
  return Math.round(value * 100) / 100;
};
